import { useState, useMemo, useCallback } from 'react';
import { ApolloError } from '@apollo/client';
import { GraphQLError } from 'graphql';
import { useTranslation } from 'react-i18next';
import { v4 as uuid } from 'uuid';
import { convertTo62 } from 'uuid-encoding';
import * as yup from 'yup';
import {
  TenantLeaseInfo,
  Unit,
  SourceType,
  SourceRelationshipIdentifier,
} from 'graphql-query-contracts';
import { RelationshipSourceType } from 'constants-entities';
import { useSaveTenantLeases } from './useSaveTenantLeases';
import { getErrorCodes, getErrorMessagesFromCodes } from 'lane-shared/helpers';
import { tenantErrorCodeTypes } from 'activate-errors';

// Type for the save function's LeaseItem (simpler version)
type SaveLeaseItem = {
  id: string;
  leaseId?: string;
  suiteId: string;
  sourceType?: SourceType;
  source?: SourceRelationshipIdentifier;
};

type EditTenantLeasesProps = {
  tenantId: string;
  propertyId: string;
  suites: Unit[];
  tenantLeases: TenantLeaseInfo[];
};

type LeaseItemErrorMessage = {
  leaseId: string;
  suiteId: string;
  sourceType: string;
  global: string;
};

export type LeaseItem = {
  id: string;
  leaseId?: string;
  suiteId?: string;
  sourceType?: SourceType;
  source?: SourceRelationshipIdentifier;
  originalSource?: SourceRelationshipIdentifier;
  isRemovable: boolean;
  availableSuites: Unit[];
  errorMessage: LeaseItemErrorMessage;
};

type LeaseItemState = {
  id: string;
  tenantId: string;
  leaseId?: string;
  suiteId?: string;
  sourceType?: SourceType;
  source?: SourceRelationshipIdentifier;
  // Track original source to prevent modification on existing leases
  originalSource?: SourceRelationshipIdentifier;
};

// Mapper functions to convert between editable and API types
const mapTenantLeaseInfoToEditable = (
  lease: TenantLeaseInfo
): LeaseItemState => {
  return {
    id: generateId(),
    tenantId: lease.tenantId,
    leaseId: lease.leaseId ?? undefined,
    suiteId: lease.suiteId ?? undefined,
    sourceType: lease.source?.sourceType ?? undefined,
    source: lease.source ?? undefined,
    // Store original source for existing leases to prevent modification
    originalSource: lease.source ?? undefined,
  };
};

const mapEditableToTenantLeaseInfo = (
  item: LeaseItemState
): TenantLeaseInfo | null => {
  // Allow suites without lease information - only require suiteId
  if (!item.suiteId) {
    return null;
  }

  return {
    tenantId: item.tenantId,
    suiteId: item.suiteId,
    leaseId: item.leaseId ?? null,
    source: item.source ?? null,
  };
};

const filterValidLeaseItems = (items: LeaseItemState[]): TenantLeaseInfo[] => {
  return items
    .map(mapEditableToTenantLeaseInfo)
    .filter((lease): lease is TenantLeaseInfo => lease !== null);
};

const errorCodeToFieldMapping: Record<
  string,
  'suiteId' | 'leaseId' | 'sourceType'
> = {
  TN02: 'suiteId',
  TN03: 'suiteId',
  TN04: 'leaseId',
  TN08: 'sourceType',
  TN09: 'leaseId',
};

const generateId = () => {
  const uniqueId = uuid();
  const shortId = convertTo62(uniqueId);

  return `ID${shortId}`;
};

// Build complete source object from sourceType and leaseId
const buildSourceObject = (
  sourceType: SourceType | undefined,
  leaseId: string | undefined
): SourceRelationshipIdentifier | undefined => {
  if (!sourceType || !leaseId) {
    return undefined;
  }

  return {
    sourceType,
    sourceRelationshipType: RelationshipSourceType.LEASE,
    sourceRelationshipId: leaseId,
  };
};

// Type guard to ensure required fields are present for SaveLeaseItem
// A suite can be saved without lease information (leaseId and sourceType are optional when suiteId is provided)
const isValidSaveLeaseItem = (
  item: LeaseItemState
): item is LeaseItemState & {
  suiteId: string;
  leaseId?: string;
  sourceType?: SourceType;
  source?: SourceRelationshipIdentifier;
} => {
  // At minimum, we need a suiteId to save
  if (!item.suiteId) {
    return false;
  }

  // If leaseId is provided, sourceType must also be provided
  if (item.leaseId && !item.sourceType) {
    return false;
  }

  // If sourceType is provided, leaseId must also be provided
  if (item.sourceType && !item.leaseId) {
    return false;
  }

  // If both leaseId and sourceType are provided, ensure source object can be built
  if (item.leaseId && item.sourceType) {
    const builtSource = buildSourceObject(item.sourceType, item.leaseId);

    if (!builtSource) {
      return false;
    }
  }

  return true;
};

const createLeaseItemState = (
  tenantId: string,
  lease?: TenantLeaseInfo
): LeaseItemState => {
  if (lease) {
    return mapTenantLeaseInfoToEditable(lease);
  }

  return {
    id: generateId(),
    tenantId,
    leaseId: undefined,
    suiteId: undefined,
    sourceType: undefined,
    source: undefined,
  };
};

const deriveInitialState = (
  tenantId: string,
  tenantLeases: TenantLeaseInfo[]
): LeaseItemState[] => {
  if (tenantLeases.length > 0) {
    return tenantLeases.map(mapTenantLeaseInfoToEditable);
  }

  return [createLeaseItemState(tenantId, undefined)];
};

const emptyLeaseItemErrorMessage: LeaseItemErrorMessage = {
  leaseId: '',
  suiteId: '',
  sourceType: '',
  global: '',
};

const getSuiteById = (suites: Unit[], suiteId: string) =>
  suites.find(s => s.id === suiteId);

const getAvailableSuites = (
  suites: Unit[],
  leaseItemsState: LeaseItemState[],
  leaseItemId: string
) => {
  const selectedSuiteIds = leaseItemsState
    .filter(item => item.id !== leaseItemId && item.suiteId)
    .map(item => item.suiteId);

  return suites.filter(suite => !selectedSuiteIds.includes(suite.id));
};

const getIsRemovable = (
  leaseItemsState: LeaseItemState[],
  item: LeaseItemState
) => {
  if (
    leaseItemsState.length === 1 &&
    !item.leaseId &&
    !item.suiteId &&
    !item.sourceType
  ) {
    return false;
  }

  return true;
};

export const useEditTenantLeases = ({
  tenantId,
  propertyId,
  suites,
  tenantLeases,
}: EditTenantLeasesProps) => {
  const { t } = useTranslation();
  const { saveLeases } = useSaveTenantLeases();

  const [leaseItemsState, setLeaseItemsState] = useState<LeaseItemState[]>(
    deriveInitialState(tenantId, tenantLeases)
  );

  const [fieldErrors, setFieldErrors] = useState<{
    [id: string]: LeaseItemErrorMessage;
  }>({});

  const [loading, setLoading] = useState(false);

  const getSuiteByIdCallback = useCallback(
    (suiteId: string) => getSuiteById(suites, suiteId),
    [suites]
  );

  const getErrorMessage = (id: string): LeaseItemErrorMessage =>
    fieldErrors[id] || { ...emptyLeaseItemErrorMessage };

  const leaseItems: LeaseItem[] = leaseItemsState.map(item => ({
    ...item,
    originalSource: item.originalSource,
    isRemovable: getIsRemovable(leaseItemsState, item),
    availableSuites: getAvailableSuites(suites, leaseItemsState, item.id),
    errorMessage: getErrorMessage(item.id),
  }));

  const selectedFloors = useMemo(() => {
    const floorSet = new Set<string>();

    leaseItemsState.forEach(item => {
      if (item.suiteId) {
        const suite = getSuiteByIdCallback(item.suiteId);

        suite?.floors?.forEach(floor => {
          if (floor?.name) {
            floorSet.add(floor.name);
          }
        });
      }
    });

    return Array.from(floorSet).join(', ');
  }, [leaseItemsState, getSuiteByIdCallback]);

  const addLeaseItem = () => {
    const newLeaseItem = createLeaseItemState(tenantId);

    setLeaseItemsState(prev => [...prev, newLeaseItem]);
  };

  const editLeaseItem = (updated: LeaseItem) => {
    setLeaseItemsState(prev =>
      prev.map(item => {
        if (item.id !== updated.id) {
          return item;
        }

        // Always build source object from current sourceType and leaseId
        // originalSource is preserved separately for removal operations
        // If leaseId or sourceType changed, this creates a new lease (old one removed, new one added)
        const newSource = buildSourceObject(
          updated.sourceType,
          updated.leaseId
        );

        return {
          ...item,
          leaseId: updated.leaseId,
          suiteId: updated.suiteId,
          sourceType: updated.sourceType,
          source: newSource,
          // Preserve originalSource unchanged - only used for identifying the lease to remove
          originalSource: item.originalSource,
        };
      })
    );
  };

  const removeLeaseItem = (leaseItemId: string) => {
    setLeaseItemsState(prev => {
      if (prev.length === 1) {
        return [createLeaseItemState(tenantId)];
      }

      return prev.filter(item => item.id !== leaseItemId);
    });
  };

  const setValidationError = (
    validationError: yup.ValidationError,
    validatedItems: LeaseItemState[]
  ) => {
    const errors: { [id: string]: LeaseItemErrorMessage } = {};
    const leaseIdFieldName: keyof LeaseItemErrorMessage = 'leaseId';
    const suiteIdFieldName: keyof LeaseItemErrorMessage = 'suiteId';
    const globalFieldName: keyof LeaseItemErrorMessage = 'global';
    const sourceTypeFieldName: keyof LeaseItemErrorMessage = 'sourceType';

    validationError.inner.forEach(error => {
      // Path format: "[2].leaseId" or "[2].suiteId"
      const match = error.path.match(
        /^\[(\d+)\]\.(leaseId|suiteId|sourceType)$/
      );

      if (match) {
        const index = Number(match[1]);
        const errorField = match[2];
        const item = validatedItems[index];
        let fieldKey: keyof LeaseItemErrorMessage;

        switch (errorField) {
          case leaseIdFieldName:
            fieldKey = leaseIdFieldName;
            break;
          case suiteIdFieldName:
            fieldKey = suiteIdFieldName;
            break;
          case sourceTypeFieldName:
            fieldKey = sourceTypeFieldName;
            break;
          default:
            fieldKey = globalFieldName;
        }

        if (item) {
          const id = item.id;

          if (!errors[id]) {
            errors[id] = { ...emptyLeaseItemErrorMessage };
          }

          errors[id][fieldKey] = error.message;
        }
      }
    });

    setFieldErrors(errors);
  };

  const setServerErrors = (
    errors: GraphQLError[],
    leaseItems: LeaseItemState[]
  ) => {
    setFieldErrors(prev => {
      const updated = { ...prev };

      errors.forEach(error => {
        const path = error.path && error.path[0];
        const leaseItem = leaseItems.find(item => item.id === path);

        if (!leaseItem) return;

        const defaultErrorMessage = error.message;
        const errorCodesError = {
          graphQLErrors: [error],
        };
        const errorCodes = getErrorCodes(errorCodesError);
        const errorMessage = getErrorMessagesFromCodes(
          errorCodes,
          tenantErrorCodeTypes,
          defaultErrorMessage
        );
        const errorCode = errorCodes[0] ?? '';
        const field = errorCodeToFieldMapping[errorCode];

        if (!updated[leaseItem.id]) {
          updated[leaseItem.id] = { ...emptyLeaseItemErrorMessage };
        }

        const leaseItemState = leaseItemsState.find(
          item => item.id === leaseItem.id
        );

        if (!leaseItemState) {
          setLeaseItemsState(prev => [
            {
              id: leaseItem.id,
              tenantId: leaseItem.tenantId,
              leaseId: leaseItem.leaseId,
              suiteId: leaseItem.suiteId,
              sourceType: leaseItem.sourceType,
            },
            ...prev,
          ]);
        }

        const nonGlobalFields = ['leaseId', 'suiteId', 'sourceType'];

        if (nonGlobalFields.includes(field)) {
          updated[leaseItem.id][field] = errorMessage;

          return;
        }

        updated[leaseItem.id].global = errorMessage;
      });

      return updated;
    });
  };

  const getLeasesToSave = (current: LeaseItemState[]) => {
    const initial = tenantLeases.map(mapTenantLeaseInfoToEditable);

    const leaseKey = (item: LeaseItemState) => {
      // Only create keys for valid lease items to avoid undefined string conversion
      if (!isValidSaveLeaseItem(item)) {
        return null;
      }

      // Use suiteId as the primary key, with leaseId and sourceType as secondary keys
      return [item.suiteId, item.leaseId, item.sourceType]
        .filter(v => v)
        .join('|');
    };

    const toAdd = current.filter(c => {
      const currentKey = leaseKey(c);

      return (
        currentKey !== null && !initial.some(i => leaseKey(i) === currentKey)
      );
    });
    const toRemove = initial.filter(i => {
      const initialKey = leaseKey(i);

      return (
        initialKey !== null && !current.some(c => leaseKey(c) === initialKey)
      );
    });

    // Convert LeaseItemState to SaveLeaseItem for saveLeases, filtering out invalid items
    const toAddForSave: SaveLeaseItem[] = toAdd
      .filter(isValidSaveLeaseItem)
      .map(item => {
        // Use the built source object from editLeaseItem
        // This will always be correctly built from current sourceType and leaseId
        return {
          id: item.id,
          suiteId: item.suiteId,
          leaseId: item.leaseId ?? undefined,
          sourceType: item.sourceType ?? undefined,
          source: item.source,
        };
      });

    const toRemoveForSave: SaveLeaseItem[] = toRemove
      .filter(isValidSaveLeaseItem)
      .map(item => {
        // For removal, use the original source as it's part of the composite ID
        // If originalSource is undefined, the lease didn't have a source originally, so remove without one
        const sourceForSave = item.originalSource ?? undefined;

        return {
          id: item.id,
          suiteId: item.suiteId,
          leaseId: item.leaseId ?? undefined,
          sourceType: item.sourceType ?? undefined,
          source: sourceForSave,
        };
      });

    return {
      toAdd: filterValidLeaseItems(toAdd),
      toRemove: filterValidLeaseItems(toRemove),
      toAddForSave,
      toRemoveForSave,
    };
  };

  const handleSaveResult = (result: any) => {
    if (result.errors?.length) {
      setServerErrors([...result.errors], leaseItemsState);

      throw new Error('An error occurred while saving lease items');
    }

    return result;
  };

  const handleSaveError = (err: unknown, filtered: LeaseItemState[]) => {
    if (err instanceof yup.ValidationError) {
      setValidationError(err, filtered);
    }

    if (err instanceof ApolloError) {
      setServerErrors([...err.graphQLErrors], leaseItemsState);
    }
  };

  const saveLeaseItems = async () => {
    // Validate all items first to provide user feedback on incomplete items
    try {
      setFieldErrors({});
      setLoading(true);
      await getValidationSchema(t).validate(leaseItemsState, {
        abortEarly: false,
      });
    } catch (err) {
      setLoading(false);

      handleSaveError(err, leaseItemsState);

      throw err;
    }

    // After validation passes, filter to only valid items for saving
    const filtered = leaseItemsState.filter(isValidSaveLeaseItem);
    const { toAddForSave, toRemoveForSave } = getLeasesToSave(filtered);

    if (toAddForSave.length === 0 && toRemoveForSave.length === 0) {
      setLoading(false);

      return;
    }

    try {
      const result = await saveLeases(
        tenantId,
        propertyId,
        toRemoveForSave,
        toAddForSave
      );

      setLoading(false);

      return handleSaveResult(result);
    } catch (err) {
      setLoading(false);

      handleSaveError(err, filtered);

      throw err;
    }
  };

  return {
    loading,
    leaseItems,
    selectedFloors,
    addLeaseItem,
    editLeaseItem,
    removeLeaseItem,
    saveLeaseItems,
  };
};

const getValidationSchema = (t: ReturnType<typeof useTranslation>['t']) => {
  const leaseItemSchema = yup.object().shape(
    {
      leaseId: yup.string().when('sourceType', {
        is: sourceType => Boolean(sourceType),
        then: yup
          .string()
          .trim()
          .required(
            t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.externalLeaseId.required'
            )
          ),
        otherwise: yup.string().notRequired(),
      }),
      suiteId: yup
        .string()
        .when('leaseId', {
          is: leaseId => Boolean(leaseId),
          then: yup
            .string()
            .required(
              t(
                'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.suite.required'
              )
            ),
          otherwise: yup.string().notRequired(),
        })
        .when('sourceType', {
          is: sourceType => Boolean(sourceType),
          then: yup
            .string()
            .trim()
            .required(
              t(
                'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.suite.required'
              )
            ),
          otherwise: yup.string().notRequired(),
        }),
      sourceType: yup.string().when('leaseId', {
        is: leaseId => Boolean(leaseId?.trim()),
        then: yup
          .string()
          .nullable(true)
          .required(
            t(
              'web.admin.channel.settings.tenantManagement.tenant.details.editLeases.externalSourceType.required'
            )
          ),
        otherwise: yup.string().nullable(true).notRequired(),
      }),
    },
    [['leaseId', 'sourceType']]
  );

  return yup.array().of(leaseItemSchema);
};
